import {
  ChangeEvent,
  CSSProperties,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './TextArea.module.css';

export type TextAreaProps = {
  textEntryClassName?: string;
  textEntryAreaClassName?: string;
  errorMessageClassName?: string;
  value: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  isAutoResize?: boolean;
  isClicked?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  style?: CSSProperties | undefined;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const TextArea = memo(
  ({
    textEntryClassName,
    textEntryAreaClassName,
    errorMessageClassName,
    value,
    onChange,
    placeholder,
    isAutoResize,
    isClicked,
    disabled,
    errorMessage,
    style,
    onFocus,
    onBlur,
  }: TextAreaProps) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const userAgentString = navigator.userAgent;
    const chromeAgent = userAgentString.indexOf('Chrome') > -1;
    let safariAgent = userAgentString.indexOf('Safari') > -1;
    if (chromeAgent && safariAgent) safariAgent = false;

    const [startCal, setStartCal] = useState(true);

    useEffect(() => {
      if (textAreaRef.current && isAutoResize) {
        // We need to reset the height momentarily to get the correct scrollHeight for the textarea
        textAreaRef.current.style.height = '0px';
        const scrollHeight =
          textAreaRef.current.scrollHeight + (safariAgent ? 20 : 5); // add 5 for android may need more height

        // We then set the height directly, outside of the render loop
        // Trying to set this with state or a ref will product an incorrect value.
        textAreaRef.current.style.height = scrollHeight + 'px';
        setStartCal(false);
      }
    }, [isAutoResize, textAreaRef, value, placeholder, startCal, safariAgent]);

    const hasError = (isClicked && !value) || errorMessage;
    return (
      <div className={textEntryClassName ?? styles.textArea}>
        <textarea
          ref={textAreaRef}
          className={`${textEntryAreaClassName ?? styles.customInput}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            backgroundColor: disabled ? '#E5E5E5' : undefined,
            overflow:
              hasError && !value
                ? 'hidden'
                : undefined /* Hide any overflowing content when there is an error */,
            border: hasError ? '1px solid #D95141' : undefined,
            ...style,
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {errorMessage ? (
          <p className={errorMessageClassName ?? styles.errorMessage}>
            {errorMessage}
          </p>
        ) : null}
      </div>
    );
  },
);
